import React, { ForwardedRef, ReactElement, ReactNode, useCallback, useMemo, useState } from 'react';
import { BaseRowData, DataProvider, DataProviderRowData } from '@adv-frontend/data-provider';
import { Virtualizer } from '@react-aria/virtualizer';
import { getPlatformStyles, isDesktop, isMobile } from '@direct-frontend/platform-code';
import { Node } from '@react-types/shared';
import { Item, useListState } from 'react-stately';
import { ListLayout, ListLayoutOptions } from '@react-stately/layout';
import { genericForwardRef, mergeRefs, useOnce, useResizeListener } from '@direct-frontend/stdlib';

import { ListStubItem } from '../ListStubItem';
import { ListSize } from '../../ListBoxBase/types';
import { List, ListProps } from '../List';
import { getScrollbarClassName } from '../../../lib/css/getScrollbarClassName';
import { useVStack } from '../../../hooks/useStack';
import { ListSection } from '../ListSection';
import { cn } from '../../../lib/css/className/className';
import { Separator } from '../../ListBoxBase/Separator/Separator';

getPlatformStyles({
    common: import('./VirtualList.css'),
    mobile: import('./VirtualList@mobile.css'),
});

const cls = cn('VirtualList');

type DataProviderRow<RowData extends BaseRowData> = ReturnType<DataProvider<RowData>['useRowData']>;

export interface VirtualListProps<RowData extends BaseRowData = BaseRowData> extends Omit<ListProps, 'children'> {
    /**
     * dataProvider для элементов
     * @link https://nda.ya.ru/t/S1VBR-zr74tgTc
     */
    dataProvider: DataProvider<RowData>;

    /**
     * Функция для отрисовки элемента. Если элемент еще не загружен, то вместо этого отрисуется `<ListStubItem />`
     * @param item элемент, возвращаемый хуком useRowData из dataProvider
     * @returns верстка элемента
     * @example item => <ListItem id={item.data.id}>{item.data.name}</ListItem>
     */
    children: (item: DataProviderRow<RowData>) => ReactElement | null;

    /**
     * Секция с элементами, которая будет запинена перед списком опций.
     * Чаще всего используется для поиска
     * @example pinnedSection={<ListInputItem placeholder="Поиск" />}
     */
    pinnedSection?: ReactNode;

    /**
     * Секция с элементами, которая будет запинена после списка опций.
     * Чаще всего используется для экшенов и переходов по ссылкам
     * @example pinnedSectionAfter={<ListItem>Добавить все</ListItem>}
     */
    pinnedSectionAfter?: ReactNode;

    /**
     * Элемент, который нужно показать, если dataProvider не вернул ни один элемент
     * @example placeholder={<ListItem>Ничего не нашлось</ListItem>}
     */
    placeholder?: ReactNode;

    /**
     * Максимальная высота списка
     * Рекомендуется передавать, если список может быть большим
     * @example maxHeight={400}
     */
    maxHeight?: number;
}

/**
 * Высота строки соответствует высоте TextInput: https://a.yandex-team.ru/arc_vcs/adv/frontend/packages/components/src/components/TextInput/TextInput.css?rev=4d49c38bd04f65c8cfeb03488487121f1982fbca#L137
 */
const rowHeightFromSize: {
    desktop: Record<ListSize, number>;
    mobile: Record<Extract<ListSize, 'm'>, number>;
} = {
    desktop: {
        m: 44,
        s: 36,
        xs: 32,
    },
    /**
     * Высота с учётом сепаратора: https://a.yandex-team.ru/arc_vcs/adv/frontend/packages/components/src/components/ListBoxBase/ListBoxBase@mobile.css?rev=4d49c38bd04f65c8cfeb03488487121f1982fbca#L45.
     * Размер только 'm' для мобильной версии.
     */
    mobile: {
        m: 61,
    },
};

interface ItemRendererProps<RowData extends BaseRowData> extends Pick<VirtualListProps<RowData>, 'dataProvider'> {
    renderer: VirtualListProps<RowData>['children'];
    node: Node<DataProviderRowData>;
}

const ItemRenderer = function ItemRenderer<RowData extends BaseRowData>(props: ItemRendererProps<RowData>) {
    const { node, renderer, dataProvider } = props;
    const item = dataProvider.useRowData(node?.index ?? 0);

    if (!item) {
        return null;
    }

    if (item.isLoading || item.isFetching) {
        return <ListStubItem width="100%" />;
    }

    if (isDesktop()) {
        return renderer(item);
    }

    const withSeparator = dataProvider.totalCount && node?.index !== dataProvider.totalCount - 1;

    return (
        <>
            {renderer(item)}
            {withSeparator && <Separator className={cls('separator')} />}
        </>
    );
};

/**
 * Компонент для создания виртуальных списков, принимает dataProvider и функцию-рендерер элементов
 *
 * Можно использовать статический массив или получать элементы из rtk api
 * @example
 * const dataProvider = useStaticDataProvider(data);
 *
 * return <VirtualList dataProvider={dataProvider}>{item => <ListItem>{item.name}</ListItem>}</VirtualList
 *
 * @example
 * const dataProvider = api.useDataProvider();
 *
 * return <VirtualList dataProvider={dataProvider}>{item => <ListItem>{item.name}</ListItem>}</VirtualList
 */
export const VirtualList = genericForwardRef(function VirtualList<RowData extends BaseRowData = BaseRowData>(
    props: VirtualListProps<RowData>,
    ref: ForwardedRef<HTMLUListElement>,
) {
    const { size, children, dataProvider, pinnedSection, pinnedSectionAfter, placeholder, className, ...listProps } =
        props;
    const scrollClass = getScrollbarClassName();

    const virtualizer = useMemo<ListLayoutOptions<DataProviderRowData>>(() => {
        const rowHeight = isMobile() ? rowHeightFromSize.mobile.m : rowHeightFromSize.desktop[size];

        return {
            estimatedRowHeight: rowHeight,
        };
    }, [size]);
    const listState = useListState<DataProviderRowData>({
        children: dataProvider.getRows().map(item => <Item key={item.id}>{item.id}</Item>),
    });
    const layout = useMemo(() => new ListLayout<DataProviderRowData>(virtualizer), [virtualizer]);
    layout.collection = listState.collection;
    const stackClass = useVStack({});

    const showPlaceholder = Boolean(placeholder) && dataProvider.totalCount === 0;
    const showSeparator = dataProvider.totalCount !== 0;

    const [listWidth, setListWidth] = useState(0);
    const resizeObserverCallback = useCallback((entries: ResizeObserverEntry[]) => {
        // Хак, чтобы перерисовывать Virtualizer при первом изменении ширины контейнера
        setListWidth(entries[0]?.contentRect.width ?? 0);
    }, []);
    const onceResizeObserverCallback = useOnce(resizeObserverCallback);
    const setListRef = useResizeListener(onceResizeObserverCallback);

    return (
        <List {...listProps} ref={mergeRefs(ref, setListRef)} className={cls('', [stackClass, className])} size={size}>
            {pinnedSection ? (
                <>
                    <ListSection>{pinnedSection}</ListSection>
                    {showSeparator && <ListSection />}
                </>
            ) : null}
            {showPlaceholder && <ListSection>{placeholder}</ListSection>}
            {layout.collection.size > 0 && (
                <Virtualizer
                    key={listWidth}
                    className={cls('list', [scrollClass])}
                    style={{ maxHeight: listProps.maxHeight }}
                    sizeToFit="height"
                    scrollDirection="vertical"
                    layout={layout}
                    collection={listState.collection}
                >
                    {(type, node) =>
                        type === 'item' ? (
                            <ItemRenderer dataProvider={dataProvider} node={node} renderer={children} />
                        ) : null
                    }
                </Virtualizer>
            )}
            {pinnedSectionAfter ? (
                <>
                    {showSeparator && !pinnedSection ? <ListSection /> : null}
                    <ListSection>{pinnedSectionAfter}</ListSection>
                </>
            ) : null}
        </List>
    );
});
